import { useSelectableTable } from '@shared/hooks';
import { TItemType, TResolvedItemsMap } from '@modules/items';
import {
  Body1Strong,
  Card,
  makeStyles,
  Subtitle2,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Link as FluentLink,
} from '@fluentui/react-components';
import { isIDetectedSelfReference } from '../../../types/references.types';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { ITableColumnSizingState } from '@shared/types';
import DetectionsRow from './DetectionRow';
import { DETECTIONS_TABLE_COLUMN_IDS } from '../../../constants';
import { IItemDefinition, TDetectionsRowItem, TDetectionsRowVariant } from '../../../types/shared.types';
import { getItemPathByItemType } from '../../../../documents';

// We are not using `useItemTableStyles` because it's not fitting our needs
const useStyles = makeStyles({
  card: { borderRadius: '12px', padding: 0 },
  table: {
    borderSpacing: '0 10px',
    '& tr': { height: '48px' },
  },
  tableBody: {
    '& > :last-child': { borderBottom: 'none' },
  },
});

type TProps<TChosenTargetItemType extends TItemType> = {
  variant?: TDetectionsRowVariant; // default 'references'
  columnSizing: ITableColumnSizingState;

  /** Group id displayed above the table:
   *  - references: sourceDocument
   *  - inline-blocks: document id (item)
   */
  sourceId: string;

  detectedElements: TDetectionsRowItem[];
  checkedRowIds: Set<string>;
  setCheckedRowIds: Dispatch<SetStateAction<Set<string>>>;

  resolvedItems: TResolvedItemsMap;

  // references-only (optional)
  chosenTargetItemMap?: Map<string, IItemDefinition<TChosenTargetItemType>>;
  setChosenTargetItemMap?: Dispatch<SetStateAction<Map<string, IItemDefinition<TChosenTargetItemType>>>>;
};

const DetectionsTable = <TChosenTargetItemType extends TItemType>({
  variant = 'references',
  columnSizing,
  sourceId,
  detectedElements,
  checkedRowIds,
  setCheckedRowIds,
  chosenTargetItemMap,
  setChosenTargetItemMap,
  resolvedItems,
}: TProps<TChosenTargetItemType>) => {
  // store all initial row ids for this table
  const initialRowIds = useMemo(() => new Set(detectedElements.map((item) => item.id)), [detectedElements]);

  // selected item ids state used to manage the selection in the table
  const selectedItemIds = new Set(Array.from(initialRowIds).filter((id) => checkedRowIds.has(id)));
  const setSelectedItemIds = (selectedIds: Set<string>) => {
    setCheckedRowIds((prev) => {
      const next = new Set(prev);
      initialRowIds.forEach((id) => (selectedIds.has(id) ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const { selectionHeaderCell, tableRows } = useSelectableTable(
    DETECTIONS_TABLE_COLUMN_IDS,
    detectedElements,
    (r) => r.id,
    selectedItemIds,
    setSelectedItemIds,
    { shouldHighlightSelected: false }
  );

  const classes = useStyles();

  const rightHeader =
    variant === 'inline-blocks'
      ? 'Matching library item' // TODO: ask Lukas and Jan
      : `Referenced ${isIDetectedSelfReference(detectedElements[0] as any) ? 'section' : 'document'}`;

  const item = resolvedItems[sourceId]?.item;
  const targetLabel = item ? item.name : 'Unknown document';
  const targetLink = item ? `/${getItemPathByItemType(item.type)}/${item._id}` : undefined;

  return (
    <div className="flex flex-col space-y-2">
      <FluentLink appearance="subtle" href={targetLink} target="_blank" onClick={(e) => e.stopPropagation()}>
        <Subtitle2>{targetLabel}</Subtitle2>
      </FluentLink>

      <Card className={classes.card}>
        <Table className={classes.table}>
          <TableHeader>
            <TableRow>
              {selectionHeaderCell}

              <TableHeaderCell {...columnSizing.getTableHeaderCellProps('detected')}>
                <Body1Strong>Detected content</Body1Strong>
              </TableHeaderCell>

              <TableHeaderCell {...columnSizing.getTableHeaderCellProps('arrow')} />

              <TableHeaderCell {...columnSizing.getTableHeaderCellProps('referenced')} className="!pr-5">
                <Body1Strong>{rightHeader}</Body1Strong>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody className={classes.tableBody}>
            {tableRows.map(({ key, item: detectedElem, selectionCell, preventSelect, ...rowAttrs }) => (
              <DetectionsRow
                key={key}
                variant={variant}
                sourceId={sourceId}
                item={detectedElem}
                selectionCell={selectionCell}
                preventSelect={preventSelect}
                resolvedItems={resolvedItems}
                chosenTargetItemMap={chosenTargetItemMap}
                setChosenTargetItemMap={setChosenTargetItemMap}
                columnSizing={columnSizing}
                {...rowAttrs}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default DetectionsTable;
